/* Donate page (manual model) — shows bank/MoMo details + a pledge/lead form.
   Also exports DonateThankYouPage. */
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, navigate, useRoute } from "../../lib/router-compat.jsx";
import PublicShell from "../../components/shell/PublicShell.jsx";
import { HeroBlock } from "../../components/SectionRenderer.jsx";
import DonationDetails from "../../components/DonationDetails.jsx";
import RichText from "../../ui/RichText.jsx";
import { Button, Field, Input, Textarea, Select, Card, Icon, BoltDeco, showToast } from "../../ui/index.js";
import { usePage, useSiteSettings } from "../../data/hooks.js";
import { addLead } from "../../data/mutations.js";

export default function DonatePage() {
  const { settings } = useSiteSettings();
  const { sections } = usePage("donate");
  const hero = sections.find((s) => s.type === "hero");
  const bodySections = sections.filter((s) => s.type === "body");

  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "Ghana", organization: "",
    amount: "", currency: "GHS", method: "either", message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Full name is required.";
    if (!form.email.trim()) e.email = "Email address is required.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.country) e.country = "Country is required.";
    if (!form.amount || Number(form.amount) <= 0) e.amount = "Enter an amount (any amount helps).";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) {
      showToast({ intent: "danger", title: "Please check the form", body: "A few fields need attention." });
      return;
    }
    setSubmitting(true);
    try {
      await addLead({
        donorName: form.name.trim(),
        donorEmail: form.email.trim(),
        donorPhone: form.phone.trim(),
        country: form.country,
        organization: form.organization.trim(),
        intendedAmount: Number(form.amount),
        currency: form.currency,
        method: form.method,
        message: form.message.trim(),
        eventId: null,
        source: "donate",
      });
      navigate(`/donate/thank-you?email=${encodeURIComponent(form.email)}`);
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't submit", body: err.message });
      setSubmitting(false);
    }
  };

  return (
    <PublicShell>
      <Helmet><title>Donate — Purple Angels Kids Foundation</title></Helmet>
      {hero && <HeroBlock section={hero} />}

      <section className="section">
        <div className="container">
          <div className="split-3-1 donate-page-row" style={{ gridTemplateColumns: "1.2fr 1fr", gap: 64 }}>
            <div>
              {bodySections.map((s) => (
                <div key={s.id} style={{ marginBottom: 40 }}>
                  <h2 className="t-3xl" style={{ marginTop: 0 }}>{s.heading}</h2>
                  <RichText html={s.bodyHtml} className="prose" />
                </div>
              ))}

              <h2 className="t-3xl" style={{ marginTop: 8 }}>How to give</h2>
              <p className="t-base muted" style={{ marginTop: 0 }}>
                Transfer directly using the details below, then submit the form so we can match your gift and thank you.
              </p>
              <div style={{ marginTop: 24 }}>
                <DonationDetails donation={settings?.donation} />
              </div>
            </div>

            <div className="donate-form-col" style={{ position: "sticky", top: 96, alignSelf: "flex-start" }}>
              <Card variant="elevated" style={{ padding: 32, boxShadow: "var(--shadow-lg)" }}>
                <h3 className="t-2xl" style={{ marginTop: 0 }}>Tell us about your gift</h3>
                <p className="t-sm muted" style={{ marginTop: 4, marginBottom: 24 }}>
                  Submit your details and we'll confirm your donation and send a receipt.
                </p>
                <form onSubmit={submit} noValidate className="stack-lg">
                  <Field label="Full name" required error={errors.name}>
                    <Input value={form.name} onChange={update("name")} error={!!errors.name} placeholder="Your full name" />
                  </Field>
                  <Field label="Email address" required error={errors.email}>
                    <Input type="email" value={form.email} onChange={update("email")} error={!!errors.email} placeholder="you@example.com" />
                  </Field>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="form-grid-2">
                    <Field label="Phone (optional)">
                      <Input type="tel" value={form.phone} onChange={update("phone")} placeholder="+233…" />
                    </Field>
                    <Field label="Country" required error={errors.country}>
                      <Select value={form.country} onChange={update("country")} error={!!errors.country}>
                        <option>Ghana</option><option>Nigeria</option><option>United Kingdom</option>
                        <option>United States</option><option>Canada</option><option>South Africa</option>
                        <option>Other</option>
                      </Select>
                    </Field>
                  </div>
                  <Field label="Organisation (optional)">
                    <Input value={form.organization} onChange={update("organization")} placeholder="If you're donating on behalf of one" />
                  </Field>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }} className="form-grid-2">
                    <Field label="Intended amount" required error={errors.amount}>
                      <Input type="number" value={form.amount} onChange={update("amount")} error={!!errors.amount} placeholder="0" min="0" />
                    </Field>
                    <Field label="Currency">
                      <Select value={form.currency} onChange={update("currency")}>
                        <option>GHS</option><option>USD</option><option>GBP</option><option>EUR</option>
                      </Select>
                    </Field>
                  </div>
                  <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                    <legend className="field__label" style={{ marginBottom: 8 }}>Preferred method</legend>
                    <div className="radio-group">
                      <label className="radio"><input type="radio" name="method" value="bank" checked={form.method === "bank"} onChange={update("method")} />Bank transfer</label>
                      <label className="radio"><input type="radio" name="method" value="momo" checked={form.method === "momo"} onChange={update("method")} />Mobile money</label>
                      <label className="radio"><input type="radio" name="method" value="either" checked={form.method === "either"} onChange={update("method")} />Either is fine</label>
                    </div>
                  </fieldset>
                  <Field label="Anything else? (optional)">
                    <Textarea value={form.message} onChange={update("message")} rows={3} placeholder="Notes for us" />
                  </Field>
                  <Button type="submit" variant="primary" size="lg" loading={submitting} className="btn--full">
                    {submitting ? "Submitting..." : "Submit my donation"}
                  </Button>
                  <p className="t-xs muted center" style={{ margin: 0 }}>
                    By submitting, you agree to our <Link to="/privacy">Privacy Notice</Link>.
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

export function DonateThankYouPage() {
  const route = useRoute();
  const email = route.query.email;
  const { settings } = useSiteSettings();
  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);

  return (
    <PublicShell>
      <section className="section section--lg" style={{ minHeight: 600, display: "grid", placeItems: "center" }}>
        <Card variant="elevated" style={{ maxWidth: 560, textAlign: "center", padding: 48, background: "var(--bg-warm)", border: 0, position: "relative", overflow: "hidden" }} role="status" aria-live="polite">
          <BoltDeco size={180} color="rgba(233, 30, 99, 0.1)" style={{ position: "absolute", top: -30, right: -30 }} />
          <BoltDeco size={140} color="rgba(244, 180, 0, 0.18)" style={{ position: "absolute", bottom: -30, left: -10 }} />
          <div style={{ position: "relative" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", background: "white", color: "var(--accent-pink)", display: "grid", placeItems: "center", margin: "0 auto 24px", boxShadow: "var(--shadow)" }}>
              <Icon.Heart size={40} />
            </div>
            <h1 ref={headingRef} tabIndex={-1} className="t-4xl" style={{ marginTop: 0, outline: "none" }}>Thank you.</h1>
            <p className="t-lg muted" style={{ marginTop: 16, lineHeight: 1.5 }}>
              {email
                ? <>We've received your details{email ? <> at <strong style={{ color: "var(--text)" }}>{email}</strong></> : null}. We'll confirm your donation and send a receipt shortly.</>
                : <>We've received your details. We'll confirm your donation and send a receipt shortly.</>}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 32, flexWrap: "wrap" }}>
              <Link to="/" className="btn btn--secondary">Back to home</Link>
              <Link to="/events" className="btn btn--ghost">See upcoming events</Link>
            </div>
            {settings?.contact?.publicEmail && (
              <p className="t-sm muted" style={{ marginTop: 32 }}>
                Questions? Reach us at <a href={`mailto:${settings.contact.publicEmail}`}>{settings.contact.publicEmail}</a>
              </p>
            )}
          </div>
        </Card>
      </section>
    </PublicShell>
  );
}
